/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useMemo, useRef } from "react";
import GenericCard from "@ui-components/Card/GenericCard";
import { GenericTable } from "@ui-components/Table/GenericTable";
import GenericSearchbar, { ParsedFilter } from "@ui-components/Searchbar/GenericSearchbar";
import GenericButton from "@ui-components/Button/GenericButton";
import { ReactComponent as IconPlus } from "@shared/assets/Icons/IconPlusV3.svg";
import { clusterColumns, Cluster } from "./ClusterData";
import { getClusters } from "./request/request";
import ClusterModal from "./ClusterModal/ClusterModal";
import { useAuth0 } from "@auth0/auth0-react";
import { filterClusters } from "@shared/components/utils/searchUtils";
import { useAppSelector } from "@shared/redux/hooks";
import { DataStateView } from "kontrolEdge/components/Backbone/BackboneDetails/StateViews";
import { TableSkeleton } from "@ui-components/Skeleton/GenericSkeleton";
import { ReflectorManagementOnboardingEmptyState } from "@shared/components/EmptyItems";
import { getTableBodyMaxHeight, REFLECTOR_V2_ROWS_PER_PAGE_OPTIONS } from "../../../helpers/tableViewport";

const suggestedKeys = [
  "Cluster ID",
  "Name",
  "Status",
  "Cloud",
  "Region",
  "Description",
  "Reflectors",
  "Last Sync",
];

const ClusterPage: React.FC = () => {
  const [activeFilters, setActiveFilters] = useState<ParsedFilter[]>([]);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isClusterModalOpen, setIsClusterModalOpen] = useState(false);
  const { getAccessTokenSilently } = useAuth0();
  const getAccessTokenSilentlyRef = useRef(getAccessTokenSilently);
  getAccessTokenSilentlyRef.current = getAccessTokenSilently;
  const [clusters, setClusters] = useState<Cluster[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

	const { tenantId } = useAppSelector(state => state.selection);
  
  // Extract fetch logic into reusable function (stable deps: no getAccessTokenSilently to avoid refetch on resize)
  const fetchClusters = React.useCallback(async () => {
    if (!tenantId) return;
    setIsLoading(true);
    setError(null);
    try {
      const token = await getAccessTokenSilentlyRef.current();
      const res = await getClusters(token, tenantId);
      const mappedClusters = res.data.map((cluster: any) => ({
        ...cluster,
        reflectors: Array.isArray(cluster.instances)
          ? cluster.instances.map((inst: any) => inst.name)
          : [],
        status: cluster.status || "Unknown",
      }));
      setClusters(mappedClusters);
      
      setRowsPerPage(10);
    } catch (err) {
      setError("Failed to load clusters.");
    } finally {
      setIsLoading(false);
    }
  }, [tenantId]);

  React.useEffect(() => {
    fetchClusters();
  }, [fetchClusters]);

  // Filtering logic using utility function
  const filteredData = useMemo(() => {
    return filterClusters(clusters, activeFilters);
  }, [activeFilters, clusters]);

  const handleCreateCluster = async (data: any) => {
    // Don't close modal immediately - let the alert show first
    // The modal will close when the user dismisses the alert
    // Refresh cluster list after successful creation
    // Wait a bit for the backend to process the creation
    await new Promise(resolve => setTimeout(resolve, 1000));
    await fetchClusters();
    // Close modal after refresh (in case alert was already dismissed)
    setIsClusterModalOpen(false);
  };

  return (
    <div className="relative">
      <GenericCard isCollapsible={false}>
        {/* Searchbar and Create Cluster button (button below searchbar) */}
        <div className="mb-2 p-4">
          <div className="flex justify-end mb-4">
            <GenericButton
              label="Create Cluster"
              variant="brand"
              size="md"
              icon={<IconPlus className="w-5 h-5" />}
              iconPosition="left"
              onClick={() => setIsClusterModalOpen(true)}
            />
          </div>
          <GenericSearchbar
            placeholder="Find by attribute or value (case-sensitive)"
            suggestedKeys={suggestedKeys}
            onFiltersChange={setActiveFilters}
          />
        </div>
        <DataStateView
          loading={isLoading}
          error={error}
          isEmpty={!isLoading && !error && clusters.length === 0}
          onRetry={fetchClusters}
          emptyContent={
            <ReflectorManagementOnboardingEmptyState heading="You have no Clusters yet" />
          }
          loadingContent={
            <TableSkeleton
              columns={clusterColumns.length}
              rows={8}
              className="mx-4 mb-4"
            />
          }
        >
          <GenericTable
            columns={clusterColumns}
            data={filteredData}
            initialRowsPerPage={rowsPerPage}
            rowsPerPageOptions={[...REFLECTOR_V2_ROWS_PER_PAGE_OPTIONS]}
            width="100%"
            tableClassName="bg-semantic-bg-primary"
            maxTableBodyHeight={getTableBodyMaxHeight(320)}
          />
        </DataStateView>
      </GenericCard>
      <ClusterModal
        isOpen={isClusterModalOpen}
        onClose={() => setIsClusterModalOpen(false)}
        onSubmit={handleCreateCluster}
      />
    </div>
  );
};

export default ClusterPage;
